import React, { useState } from "react";

// Security Insights: which frameworks the page shows (GRC point 2). The organisation's default comes
// from the controls summary (shownFrameworks: canonical names, null = every framework): a list a GRC
// manager saved, else NERC CIP only where NERC CIP applies. Anyone may show other frameworks for this
// visit; a manager may save the selection as the organisation's default. Display only: no score,
// mapping or risk changes with it.

// The canonical name of a framework key as the summary names it (backend key, e.g. "SOC2" or
// "NERC CIP", → canonical name, e.g. "SOC 2"); a key with no metadata is its own name.
export const canonicalOf = (key, canonicalByKey) => canonicalByKey?.[key] || key;

// Whether a framework (canonical name) is in the view; null shown = every framework.
export const isShown = (canonical, shown) => shown == null || shown.includes(canonical);

// A control's mappings within the view (frameworkMappings carry backend keys).
export const mappingsInView = (mappings, shown, canonicalByKey) =>
    shown == null ? (mappings || []) : (mappings || []).filter(m => isShown(canonicalOf(m.framework, canonicalByKey), shown));

// The controls with only the view's mappings, for the views that list mappings (Issues).
export const controlsInView = (controls, shown, canonicalByKey) =>
    shown == null ? controls : (controls || []).map(c => ({ ...c, frameworkMappings: mappingsInView(c.frameworkMappings, shown, canonicalByKey) }));

// The estimates (one per framework, canonical names) within the view.
export const estimatesInView = (estimates, shown) => (estimates || []).filter(e => isShown(e.framework, shown));

// The frameworks a new audit can be of, the ones in the view first (so it starts on NERC CIP for a NERC organisation).
export const auditFrameworks = (estimates, shown) =>
    [...new Set([...estimatesInView(estimates, shown), ...(estimates || [])].map(e => e.framework))];

// Toggling a framework: off leaves at least one framework shown; on keeps the estimates' order.
export const toggled = (shown, framework, all) => {
    const current = shown == null ? all : shown;
    const next = current.includes(framework) ? current.filter(f => f !== framework) : [...current, framework];
    if (next.length === 0) return current;
    const ordered = all.filter(f => next.includes(f));
    return ordered.length === all.length ? null : ordered;
};

const sameList = (a, b) => (a == null && b == null) || (a != null && b != null && a.length === b.length && a.every(f => b.includes(f)));

// The picker above the tabs. frameworks: every framework the organisation can show, canonical names in
// display order. shown: the current view. orgDefault / orgDefaultSaved: the summary's shownFrameworks and
// shownFrameworksSaved. onChange(view): this visit's view. onSave(list | []): saves ([] restores the
// built-in default); canSave: a GRC manager (the server checks again). label(canonical) → chip text.
const GrcFrameworkView = ({ frameworks, shown, orgDefault, orgDefaultSaved, onChange, canSave, onSave, label = (f) => f }) => {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    if (!frameworks || frameworks.length < 2) return null;

    const differsFromDefault = !sameList(shown, orgDefault);
    const save = async (list) => {
        setSaving(true);
        setError(null);
        try {
            await onSave(list);
        } catch {
            setError("Could not save the default view. Try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 flex flex-wrap items-center gap-2" data-testid="grc-framework-view">
            <span className="text-xs font-semibold text-gray-600">Frameworks shown</span>
            {frameworks.map(f => {
                const on = isShown(f, shown);
                return (
                    <button key={f} type="button" aria-pressed={on} onClick={() => onChange(toggled(shown, f, frameworks))}
                        data-testid={`grc-framework-view-${f}`}
                        className={`text-xs px-2 py-0.5 rounded-full border ${on
                            ? "bg-indigo-600 border-indigo-600 text-white"
                            : "bg-white border-gray-300 text-gray-600 hover:border-indigo-400"}`}>
                        {label(f)}
                    </button>
                );
            })}
            {shown != null && (
                <button type="button" onClick={() => onChange(null)} className="text-xs text-indigo-600 hover:underline">Show all</button>
            )}
            <span className="ml-auto flex items-center gap-2">
                {differsFromDefault && (
                    <button type="button" onClick={() => onChange(orgDefault)} className="text-xs text-gray-600 hover:underline">
                        Back to the organisation&apos;s default
                    </button>
                )}
                {canSave && differsFromDefault && (
                    <button type="button" disabled={saving} onClick={() => save(shown ?? frameworks)}
                        data-testid="grc-framework-view-save"
                        className="text-xs px-2 py-1 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
                        {saving ? "Saving…" : "Save as the organisation's default"}
                    </button>
                )}
                {canSave && orgDefaultSaved && !differsFromDefault && (
                    <button type="button" disabled={saving} onClick={() => save([])}
                        data-testid="grc-framework-view-reset" className="text-xs text-gray-600 hover:underline disabled:opacity-50">
                        Use the built-in default
                    </button>
                )}
            </span>
            {error && <div className="w-full text-xs text-red-600" role="alert">{error}</div>}
        </div>
    );
};

export default GrcFrameworkView;
